// chatV4.js - Serviço central de conversa com o cérebro unificado (n8n JoinKids_Chat_v4)
// Todas as caixas de conversa (Lulu/Nando, Idiomas/Bíblico) usam este serviço.
//
// O progresso da criança (nível, palavras aprendidas) mora no BACKEND (chat-progresso).
// O navegador (localStorage) funciona como cópia local e plano B quando o backend
// não responder — a criança nunca fica sem resposta.

import { GET, POST, API_ENDPOINT } from './api';
import { ReadObject } from './storage';

// O chat agora passa pelo BACKEND (porteiro), que fala com o n8n usando um
// segredo guardado no servidor. Assim o endereço e a "senha" do n8n NUNCA
// aparecem no navegador, e ninguém de fora consegue chamar o cérebro.
const CHAT_URL = `${API_ENDPOINT}/chat`;

export const NIVEIS = ['explorador', 'aventureiro', 'campeao'];
export const NOME_DOS_NIVEIS = {
  explorador: 'Explorador 🌱',
  aventureiro: 'Aventureiro 🌿',
  campeao: 'Campeão 🌳',
};

// Quantos sinais seguidos de "subir"/"descer" são necessários para mudar de nível (plano B local)
const SINAIS_PARA_MUDAR = 3;
const MAX_PALAVRAS_GUARDADAS = 12;

// Guarda em memória quais perfis já tentamos sincronizar com o backend nesta sessão
// (marcamos como "tentado" mesmo se falhar, para não insistir num backend fora do ar a cada mensagem)
const jaTentouSincronizar = {};

const chaveNivel = (dono, personagem) => `jk_nivel_${personagem}_${dono || 'guest'}`;
const chaveSinal = (dono, personagem) => `jk_sinal_${personagem}_${dono || 'guest'}`;
const chavePalavras = (dono, personagem) => `jk_palavras_${personagem}_${dono || 'guest'}`;
const chaveIdioma = (dono) => `jk_idioma_${dono || 'guest'}`;

export const getNivel = (dono, personagem) => {
  const salvo = localStorage.getItem(chaveNivel(dono, personagem));
  return NIVEIS.includes(salvo) ? salvo : 'explorador';
};

export const getIdiomaAlvo = (dono) => localStorage.getItem(chaveIdioma(dono)) || '';
export const setIdiomaAlvo = (dono, idioma) => localStorage.setItem(chaveIdioma(dono), idioma);

const getPalavrasRecentes = (dono, personagem) => {
  try {
    const lista = JSON.parse(localStorage.getItem(chavePalavras(dono, personagem)) || '[]');
    return Array.isArray(lista) ? lista : [];
  } catch (e) {
    return [];
  }
};

const guardarLocal = (dono, personagem, nivel, palavras) => {
  if (nivel && NIVEIS.includes(nivel)) {
    localStorage.setItem(chaveNivel(dono, personagem), nivel);
  }
  if (Array.isArray(palavras)) {
    localStorage.setItem(chavePalavras(dono, personagem), JSON.stringify(palavras));
  }
};

// Busca o progresso no backend uma vez por sessão e atualiza a cópia local.
// Tenta no máximo UMA vez por sessão (mesmo se falhar), para não travar nem
// insistir num backend que ainda não foi publicado.
const sincronizarComBackend = async (profileId, personagem) => {
  if (!profileId) return;
  const marca = `${profileId}_${personagem}`;
  if (jaTentouSincronizar[marca]) return;
  jaTentouSincronizar[marca] = true; // marca ANTES de tentar: uma tentativa por sessão
  try {
    const resposta = await GET(`/chat-progressos?profile=${profileId}&personagem=${personagem}`, true);
    if (resposta && resposta.nivel) {
      guardarLocal(profileId, personagem, resposta.nivel, resposta.palavras_recentes);
    }
  } catch (e) {
    // Backend ainda não publicado ou fora do ar: seguimos com a cópia local, sem barulho
  }
};

// Atualiza o progresso NO NAVEGADOR e devolve o novo nível quando a criança sobe
// (ou null). É instantâneo — a resposta nunca espera por isto.
const atualizarProgressoLocal = (notinha, dono, personagem, modo) => {
  if (!notinha || modo !== 'idiomas') return null;

  if (Array.isArray(notinha.palavras_ensinadas) && notinha.palavras_ensinadas.length > 0) {
    const atuais = getPalavrasRecentes(dono, personagem);
    const juntas = [...atuais, ...notinha.palavras_ensinadas]
      .filter((p, i, arr) => typeof p === 'string' && arr.indexOf(p) === i)
      .slice(-MAX_PALAVRAS_GUARDADAS);
    localStorage.setItem(chavePalavras(dono, personagem), JSON.stringify(juntas));
  }

  if (!notinha.sinal_nivel) return null;

  let sinal = parseInt(localStorage.getItem(chaveSinal(dono, personagem)) || '0', 10);
  if (notinha.sinal_nivel === 'subir') sinal += 1;
  else if (notinha.sinal_nivel === 'descer') sinal -= 1;

  const nivelAtual = getNivel(dono, personagem);
  const posicao = NIVEIS.indexOf(nivelAtual);

  if (sinal >= SINAIS_PARA_MUDAR && posicao < NIVEIS.length - 1) {
    const novoNivel = NIVEIS[posicao + 1];
    localStorage.setItem(chaveNivel(dono, personagem), novoNivel);
    localStorage.setItem(chaveSinal(dono, personagem), '0');
    return novoNivel;
  }
  if (sinal <= -SINAIS_PARA_MUDAR && posicao > 0) {
    const novoNivel = NIVEIS[posicao - 1];
    localStorage.setItem(chaveNivel(dono, personagem), novoNivel);
    localStorage.setItem(chaveSinal(dono, personagem), '0');
    return null; // descer de nível é silencioso — nunca desanimar a criança
  }
  localStorage.setItem(chaveSinal(dono, personagem), String(sinal));
  return null;
};

// Envia a notinha ao backend EM SEGUNDO PLANO (não trava a resposta da criança).
// Quando o backend responde, reconcilia a cópia local com o valor oficial.
const registrarNotinhaBackend = (notinha, profileId, dono, personagem, modo) => {
  if (!notinha || !profileId) return;
  POST(`/chat-progressos?profile=${profileId}`, { data: { personagem, modo, notinha } }, true)
    .then((resposta) => {
      if (resposta && typeof resposta.subiu !== 'undefined') {
        guardarLocal(dono, personagem, resposta.nivel, resposta.palavras_recentes);
      }
    })
    .catch(() => {
      // Backend ainda não publicado ou fora do ar: o progresso local já foi salvo, tudo certo
    });
};

/**
 * Envia uma mensagem ao cérebro v4 e devolve { answer, conversationId, subiuDeNivel, mensagemNivel }.
 * Lança erro se o n8n não responder — quem chama decide o plano B (Dify).
 */
export const enviarMensagemV4 = async ({ query, conversationId, personagem, modo, nome, userId, profileId }) => {
  const dono = profileId || userId;

  // Busca o nível mais recente no backend (uma vez por sessão, sem travar a conversa se falhar)
  if (modo === 'idiomas') {
    await sincronizarComBackend(profileId, personagem);
  }

  const corpo = JSON.stringify({
    query,
    conversation_id: conversationId || null,
    personagem,
    modo,
    nivel: getNivel(dono, personagem),
    nome: nome || '',
    idioma: modo === 'idiomas' ? getIdiomaAlvo(dono) : '',
    palavras_recentes: modo === 'idiomas' ? getPalavrasRecentes(dono, personagem) : [],
    user: nome || 'guest',
    user_id: userId || 'guest_id',
  });

  // Cabeçalhos com o login da criança (JWT), para o backend autorizar a chamada
  const cabecalhos = { 'Content-Type': 'application/json' };
  try {
    const auth = await ReadObject('authentication');
    if (auth && auth.jwt) {
      cabecalhos.Authorization = `Bearer ${auth.jwt}`;
    }
  } catch (e) {
    // sem login salvo: o backend responde 403 e a tela trata
  }

  // Plano B embutido: se a primeira tentativa falhar, espera um pouco e tenta de novo.
  // Só depois de 2 falhas o erro sobe para a tela mostrar o aviso amigável.
  let dados = null;
  let ultimoErro = null;
  for (let tentativa = 1; tentativa <= 2; tentativa++) {
    try {
      const resposta = await fetch(CHAT_URL, {
        method: 'POST',
        headers: cabecalhos,
        body: corpo,
      });
      if (!resposta.ok) {
        throw new Error(`Backend do chat respondeu com status: ${resposta.status}`);
      }
      const recebido = await resposta.json();
      if (!recebido.answer) {
        throw new Error('Resposta do chat não contém answer');
      }
      dados = recebido;
      break;
    } catch (erro) {
      ultimoErro = erro;
      if (tentativa === 1) {
        console.warn('Primeira tentativa falhou, tentando de novo em 1s...', erro);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  }
  if (!dados) {
    throw ultimoErro;
  }

  // Atualiza o nível AGORA (instantâneo, no navegador) para a resposta não esperar.
  const novoNivel = atualizarProgressoLocal(dados.notinha, dono, personagem, modo);

  // Persiste no backend em segundo plano — não segura a resposta da criança.
  registrarNotinhaBackend(dados.notinha, profileId, dono, personagem, modo);

  return {
    answer: dados.answer,
    conversationId: dados.conversation_id || conversationId,
    subiuDeNivel: Boolean(novoNivel),
    mensagemNivel: novoNivel
      ? `🎉 Parabéns! Você subiu para o nível ${NOME_DOS_NIVEIS[novoNivel]}! Continue assim!`
      : '',
  };
};
